import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import config from '../config';
import CharacterTable from './CharacterTable';
import InfiniteScroll from 'react-infinite-scroll-component';

interface Character {
  _id: string;
  characterName: string;
  characterSurname: string;
  server: string;
  class: string;
  race: string;
  level: number;
  displayedGuildId?: string;
  displayedGuild?: {
    _id: string;
    name: string;
  };
  auth0Id: string;
  ownerName: string | null;
}

const ITEMS_PER_PAGE = 20;

const MyCharacters: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchCharacters = async (pageNum: number) => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();

      const response = await axios.get<{ characters: Character[]; total: number; pages: number }>(
        `${config.api.baseURL}/api/characters`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            page: pageNum,
            limit: ITEMS_PER_PAGE,
            mine: true // Request only user's characters
          }
        }
      );


      if (pageNum === 1) {
        setCharacters(response.data.characters);
      } else {
        setCharacters(prev => [...prev, ...response.data.characters]);
      }

      setHasMore(pageNum < response.data.pages);
      setPage(pageNum);
    } catch (error) {
      console.error('MyCharacters: Error fetching characters:', error);
      setError('Failed to fetch characters');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCharacters(1);
  }, [getAccessTokenSilently, user?.sub]);

  const loadMore = () => {
    if (!isLoading && hasMore) {
      fetchCharacters(page + 1);
    }
  };

  const handleDelete = async (characterId: string) => {
    if (!window.confirm('Are you sure you want to delete this character?')) {
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${config.api.baseURL}/api/characters/${characterId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setCharacters(prev => prev.filter(c => c._id !== characterId));
    } catch (error) {
      console.error('Error deleting character:', error);
      setError('Failed to delete character');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          My Characters
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/add-character')}
        >
          Add Character
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <InfiniteScroll
        dataLength={characters.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        }
        endMessage={
          <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
            {characters.length > 0 ? "No more characters to load." : "No characters found."}
          </Typography>
        }
      >
        <CharacterTable 
          characters={characters}
          onDelete={handleDelete}
        />
      </InfiniteScroll>
    </Box>
  );
};

export default MyCharacters; 